import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Dialog, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  ChipToggleGroup,
  EmptyState,
  GradientText,
  Input,
  PhotoGridTile,
} from '@/design-system'
import { StudioLoadingVeil } from '@/features/gallery-studio-3d/components/StudioLoadingVeil'
import { EgowallLogo, LogoMark } from '../../components/EgowallLogo'
import { AlbumHero } from '../../components/AlbumHero'
import { Lightbox } from '../../components/Lightbox'
import { usePublicAlbum } from '../../hooks/usePublicAlbum'
import { useAlbumLike } from '../../hooks/useAlbumLike'
import { AlbumPhoto } from '../../types'

type PhotoFilter = 'all' | 'most-loved' | 'with-comments'

const FILTERS = [
  { value: 'all', label: 'All photos' },
  { value: 'most-loved', label: 'Most loved' },
  { value: 'with-comments', label: 'With comments' },
]

const guestNameKey = (shareId: string) => `egowall.guestName.${shareId}`

const TopBar = styled(Box)({
  height: 54,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '0 22px',
  position: 'sticky',
  top: 0,
  zIndex: 40,
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--ds-border)',
  '@media (max-width: 640px)': {
    padding: '0 14px',
  },
})

const KeyMeta = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.12em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  '@media (max-width: 720px)': { display: 'none' },
})

const Wel = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.16em',
  color: 'var(--ds-accent-dot-1, #ffcf7d)',
  textTransform: 'uppercase',
  marginBottom: 12,
})

const HeroTitle = styled(Typography)({
  color: '#fff',
  fontSize: 'clamp(32px, 5vw, 52px)',
  fontWeight: 750,
  letterSpacing: '-0.045em',
  lineHeight: 1.05,
})

const HeroSub = styled(Box)({
  marginTop: 10,
  display: 'flex',
  justifyContent: 'center',
  gap: 16,
  flexWrap: 'wrap',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  color: 'rgba(255,255,255,0.6)',
})

const Crowd = styled(Box)({
  marginTop: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
})

const LiveDot = styled('i')({
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--ds-accent-dot-4, #01dafe)',
  boxShadow: '0 0 8px var(--ds-accent-dot-4, #01dafe)',
  marginRight: 6,
  fontStyle: 'normal',
})

const BodyWrap = styled(Box)({
  maxWidth: 1100,
  margin: '0 auto',
  padding: '26px 28px 40px',
  '@media (max-width: 640px)': { padding: '20px 16px 40px' },
})

const Tools = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  paddingBottom: 20,
})

const ToolNote = styled(Typography)({
  marginLeft: 'auto',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
  '@media (max-width: 720px)': { width: '100%', marginLeft: 0, marginTop: 4 },
})

// See AlbumViewScreen's MasonryGrid — CSS multi-column is column-major
// (fills column 1 top-to-bottom before starting column 2), which turned a
// 4-photo album into an uneven 2-1-1 split instead of 3-then-1. A real grid
// is row-major; `auto-fit` collapses unused column tracks so a short last
// row or a lone photo gets real width instead of a narrow empty-track gap.
const Masonry = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
  gap: 16,
  alignItems: 'start',
})

const Contrib = styled(Box)({
  margin: '26px 0',
  border: '1.5px dashed var(--ds-border-strong)',
  borderRadius: 'var(--ds-radius-lg)',
  padding: 26,
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  background: '#fff',
  cursor: 'pointer',
  transition: 'border-color var(--ds-duration-normal) var(--ds-ease)',
  '&:hover': { borderColor: 'var(--ds-accent-deep-b, #8b5cf6)' },
  '@media (max-width: 640px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
  },
})

const ContribIcon = styled(Box)({
  width: 46,
  height: 46,
  borderRadius: 14,
  background: 'var(--ds-gradient-spectrum-soft)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  flexShrink: 0,
})

const WalkBand = styled(Box)({
  position: 'relative',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas, #0e0d12)',
  color: 'var(--ds-night-ink, #f4f3f6)',
  marginBottom: 30,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 55% 80% at 80% 30%, rgba(190,157,253,0.2), transparent 62%)',
    pointerEvents: 'none',
  },
})

const WalkIn = styled(Box)({
  position: 'relative',
  zIndex: 2,
  padding: '34px 38px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 24,
  flexWrap: 'wrap',
  '@media (max-width: 640px)': { padding: '28px 22px' },
})

const Convert = styled(Box)({
  textAlign: 'center',
  padding: '34px 0 60px',
})

const IdPaper = styled(Box)({
  width: 380,
  maxWidth: '100%',
  background: '#fff',
  borderRadius: 20,
  padding: 30,
  textAlign: 'center',
  boxShadow: 'var(--ds-shadow-lift)',
})

const SpectrumMark = styled(Box)({
  width: 46,
  height: 46,
  margin: '0 auto 16px',
  borderRadius: 14,
  background: 'var(--ds-gradient-spectrum)',
})

// Shared shell for the "can't show you this album" states (private /
// unavailable). By the time we know a link is locked, the backend has
// already withheld everything about it — no title, no cover — so the copy
// stays deliberately generic rather than faking a quote we don't have.
// Still built as a real marketing beat (the actual mark, a headline, a
// pitch, two CTAs) instead of a bare error page — a locked door is still a
// chance to sell the product.
function GuestGate({
  eyebrow,
  title,
  description,
  navigate,
}: {
  eyebrow: string
  title: string
  description: string
  navigate: ReturnType<typeof useNavigate>
}) {
  return (
    <Box maxWidth={460} mx='auto' px={3} py={10} textAlign='center'>
      <LogoMark size={44} sx={{ margin: '0 auto 20px' }} />
      <GradientText variant='eyebrow'>{eyebrow}</GradientText>
      <Typography
        sx={{
          mt: '10px',
          fontSize: 27,
          fontWeight: 760,
          letterSpacing: '-0.035em',
          lineHeight: 1.15,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ mt: '12px', fontSize: 14, color: 'var(--ds-ink-muted)', lineHeight: 1.6 }}>
        {description}
      </Typography>
      <Box mt={3.5} display='flex' flexDirection='column' alignItems='center' gap={1.25}>
        <Button variant='spectrum' size='lg' onClick={() => navigate('/')}>
          Make your own — free <span aria-hidden>→</span>
        </Button>
        <Button variant='ghost' size='sm' onClick={() => navigate('/discover')}>
          Browse public albums &amp; galleries
        </Button>
      </Box>
      <Typography
        sx={{
          mt: 2.5,
          fontFamily: 'var(--ds-font-mono)',
          fontSize: 8.5,
          letterSpacing: '0.12em',
          color: 'var(--ds-ink-subtle)',
          textTransform: 'uppercase',
        }}
      >
        Free forever plan · Full resolution · No credit card
      </Typography>
    </Box>
  )
}

export function GuestAlbumScreen() {
  const { shareId = '' } = useParams()
  const navigate = useNavigate()
  const { album, isPrivate, loading, error } = usePublicAlbum(shareId)
  const [photos, setPhotos] = useState<AlbumPhoto[]>([])
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('all')
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [guestbookOpen, setGuestbookOpen] = useState(false)
  const [guestName, setGuestName] = useState('')
  const [nameDraft, setNameDraft] = useState('')
  const [pendingLike, setPendingLike] = useState<AlbumPhoto | null>(null)

  const { toggleLike } = useAlbumLike(shareId, photos, setPhotos)

  useEffect(() => {
    if (album?.photos) setPhotos(album.photos)
  }, [album?.photos])

  useEffect(() => {
    if (!shareId || typeof window === 'undefined') return
    const stored = sessionStorage.getItem(guestNameKey(shareId))
    if (stored) setGuestName(stored)
  }, [shareId])

  useEffect(() => {
    if (lightboxIndex == null) return
    if (photos.length === 0) {
      setLightboxIndex(null)
      return
    }
    if (lightboxIndex >= photos.length) setLightboxIndex(photos.length - 1)
  }, [photos, lightboxIndex])

  const title = album?.title || 'Album'
  const keyCode = useMemo(() => {
    const raw = String(shareId || '')
    const tail = raw.slice(-4).toUpperCase() || 'GUEST'
    return `KEY ${tail}`
  }, [shareId])

  const visiblePhotos = useMemo(() => {
    if (photoFilter === 'most-loved') {
      return [...photos].sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))
    }
    if (photoFilter === 'with-comments') {
      return photos.filter((p) => (p.commentCount || 0) > 0)
    }
    return photos
  }, [photos, photoFilter])

  const ensureGuest = useCallback(
    (then?: () => void) => {
      if (guestName) {
        then?.()
        return
      }
      setGuestbookOpen(true)
    },
    [guestName],
  )

  const commitGuestbook = () => {
    const name = nameDraft.trim()
    if (!name) return
    setGuestName(name)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(guestNameKey(shareId), name)
    }
    setGuestbookOpen(false)
    setNameDraft('')
    if (pendingLike) {
      const photo = pendingLike
      setPendingLike(null)
      void toggleLike(photo)
    }
  }

  const onLike = (photo: AlbumPhoto) => {
    if (!guestName) {
      setPendingLike(photo)
      setGuestbookOpen(true)
      return
    }
    void toggleLike(photo)
  }

  if (loading) {
    return <StudioLoadingVeil label='OPENING ALBUM…' />
  }

  if (isPrivate) {
    return (
      <GuestGate
        eyebrow='Private album'
        title="This one's locked"
        description="Its owner hasn't shared this album publicly — ask them for a fresh link, or check back later."
        navigate={navigate}
      />
    )
  }

  if (error || !album) {
    return (
      <GuestGate
        eyebrow='Link unavailable'
        title="We couldn't open this album"
        description='The link may be broken, expired, or the album was removed.'
        navigate={navigate}
      />
    )
  }

  return (
    <Box sx={{ background: 'var(--ds-surface)', minHeight: '100vh' }}>
      <TopBar>
        <EgowallLogo onClick={() => navigate('/')} />
        <KeyMeta>
          Guest · {keyCode} · No account needed
        </KeyMeta>
        <Button variant='secondary' size='sm' onClick={() => setGuestbookOpen(true)}>
          {guestName ? `Hi, ${guestName}` : 'Sign the guestbook'}
        </Button>
      </TopBar>

      <AlbumHero
        photos={photos}
        coverUrl={album?.coverUrl}
        variant='guest'
        plateSx={{ left: '50%', right: 'auto', transform: 'translateX(-50%)', textAlign: 'center', maxWidth: 420 }}
      >
        <Wel>You&apos;ve been given a key</Wel>
        <HeroTitle>{title}</HeroTitle>
        <HeroSub>
          <span>
            {photos.length} PHOTO{photos.length === 1 ? '' : 'S'} · FULL RESOLUTION
          </span>
        </HeroSub>
        {!!album.viewerCount && album.viewerCount > 0 && (
          <Crowd>
            <Typography
              sx={{
                fontFamily: 'var(--ds-font-mono)',
                fontSize: 9.5,
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              <LiveDot />
              {album.viewerCount} PEOPLE HERE NOW
            </Typography>
          </Crowd>
        )}
      </AlbumHero>

      <BodyWrap>
        <Tools>
          <ChipToggleGroup
            options={FILTERS}
            value={photoFilter}
            onChange={(v) => setPhotoFilter(v as PhotoFilter)}
          />
          <ToolNote>Tap ❤ to react — first name only, no signup</ToolNote>
        </Tools>

        {photos.length === 0 ? (
          <EmptyState title='No photos yet' description='The owner hasn’t added photos.' />
        ) : (
          <Masonry>
            {visiblePhotos.map((p) => {
              const gridIndex = photos.findIndex((x) => x.id === p.id)
              return (
                <PhotoGridTile
                  key={p.id}
                  src={p.url || p.thumbUrl || ''}
                  liked={p.liked}
                  likeCount={p.likeCount}
                  onClick={() => setLightboxIndex(gridIndex >= 0 ? gridIndex : 0)}
                  onLikeToggle={() => onLike(p)}
                />
              )
            })}
          </Masonry>
        )}

        <Contrib
          role='button'
          tabIndex={0}
          onClick={() =>
            ensureGuest(() => {
              /* Guest upload not wired — guestbook is the gate for now */
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              ensureGuest()
            }
          }}
        >
          <ContribIcon aria-hidden>📸</ContribIcon>
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 680 }}>
              You were there — add your photos
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'var(--ds-ink-muted)', mt: '3px' }}>
              Your angle belongs in this album. The host reviews everything before it appears.
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 9,
              letterSpacing: '0.1em',
              color: 'var(--ds-accent-deep-b, #8b5cf6)',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Coming soon
          </Typography>
        </Contrib>

        {album?.galleryId && (
          <WalkBand>
            <WalkIn>
              <Box maxWidth={430}>
                <GradientText variant='eyebrow'>The part you&apos;ll talk about tomorrow</GradientText>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 23,
                    fontWeight: 720,
                    letterSpacing: '-0.03em',
                    color: 'var(--ds-night-ink, #f4f3f6)',
                  }}
                >
                  These photos hang in a real room.
                </Typography>
                <Typography
                  sx={{
                    mt: '7px',
                    fontSize: 13,
                    color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
                  }}
                >
                  Walk a 3D gallery of this album right from your phone — no app.
                </Typography>
              </Box>
              <Button variant='night' size='lg' onClick={() => navigate(`/g/${album.galleryId}`)}>
                ◈ Walk the gallery <span aria-hidden>→</span>
              </Button>
            </WalkIn>
          </WalkBand>
        )}

        <Convert>
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.025em' }}>
            Your photos deserve this too.
          </Typography>
          <Typography
            sx={{
              m: '10px auto 20px',
              maxWidth: 380,
              fontSize: 13,
              color: 'var(--ds-ink-muted)',
              lineHeight: 1.6,
            }}
          >
            This album was made in under a minute. Trips, birthdays, first years — give yours a
            place people can visit.
          </Typography>
          <Button variant='spectrum' size='lg' onClick={() => navigate('/')}>
            Make your own — free <span aria-hidden>→</span>
          </Button>
          <Typography
            sx={{
              mt: 1.75,
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 8.5,
              letterSpacing: '0.12em',
              color: 'var(--ds-ink-subtle)',
              textTransform: 'uppercase',
            }}
          >
            Free forever plan · Full resolution · No credit card
          </Typography>
        </Convert>
      </BodyWrap>

      <Lightbox
        open={lightboxIndex != null && photos.length > 0}
        photos={photos}
        index={lightboxIndex ?? 0}
        albumId={shareId}
        albumTitle={title}
        albumOwnerId={album.ownerId}
        allowDownload={album.allowDownload !== false}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        onToggleLike={(photo) => onLike(photo)}
      />

      <Dialog
        open={guestbookOpen}
        onClose={() => {
          setGuestbookOpen(false)
          setPendingLike(null)
        }}
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            m: 2,
            maxWidth: 420,
            width: '100%',
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(14,13,18,0.55)',
              backdropFilter: 'blur(6px)',
            },
          },
        }}
      >
        <IdPaper>
          <SpectrumMark aria-hidden />
          <Typography sx={{ fontSize: 19, fontWeight: 720, letterSpacing: '-0.025em' }}>
            Sign the guestbook
          </Typography>
          <Typography
            sx={{ m: '10px 0 18px', fontSize: 13, color: 'var(--ds-ink-muted)', lineHeight: 1.55 }}
          >
            A first name is all this album needs — so the host knows who loved what. No password, no
            email, no account.
          </Typography>
          <Input
            variant='filled'
            fullWidth
            autoFocus
            placeholder='Your first name'
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitGuestbook()
            }}
            sx={{
              textAlign: 'center',
              background: '#fff',
              boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
              '&.Mui-focused': {
                boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-deep-b, #8b5cf6)',
              },
              '& input': { textAlign: 'center' },
            }}
          />
          <Button
            variant='spectrum'
            size='lg'
            onClick={commitGuestbook}
            disabled={!nameDraft.trim()}
            sx={{ width: '100%', mt: 1.5 }}
          >
            That&apos;s me <span aria-hidden>→</span>
          </Button>
          <Typography
            sx={{
              mt: 1.75,
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 7.5,
              letterSpacing: '0.1em',
              color: 'var(--ds-ink-subtle)',
              textTransform: 'uppercase',
              lineHeight: 1.7,
            }}
          >
            Stored for this album only · Visible to the host · Nothing else created
          </Typography>
        </IdPaper>
      </Dialog>
    </Box>
  )
}
